import React, { useRef } from "react";
import clsx from "clsx";

import { ReactComponent as ArrowIcon } from "../../../assets/svg/arrow_icon.svg";
import { ReactComponent as CloseIcon } from "../../../assets/svg/close_outline.svg";
import audioTimeEnd from "../../../assets/audio/timer-end.mp3";
import Button from "../../../atoms/Button";

import styles from "./Timer.module.scss";

export interface IPropsTimer {
  className?: string;
  onClose: () => void;
}

const DEFAULT_TIMER = {
  minutes: "05",
  seconds: "00",
  counter: 300,
};

const formatNumberToStr = (num: number) => {
  return num < 10 ? `0${num}` : `${num}`;
};

const formatStr = (str: string) => {
  return str.replace(/[^0-9]/g, "");
};

const formatStrToNumber = (str: string) => {
  const s = str
    .replace(/[^0-9]/g, "")
    .replace(/^0+/, "")
    .substring(0, 2);
  return Math.min(Number(s), 59);
};

const Timer: React.FC<IPropsTimer> = props => {
  const [timer, setTimer] = React.useState<{
    minutes: string;
    seconds: string;
    counter: number;
  }>(DEFAULT_TIMER);
  const [isStart, setIsStart] = React.useState<boolean>(false);
  const timerId = useRef<ReturnType<typeof setTimeout> | null>();
  const audio = useRef<HTMLAudioElement | null>(null);
  const unitSeconds = 1000;

  const setMinutes = (str: string, completed: boolean) => {
    const minutes = formatStrToNumber(str);
    const seconds = Number(timer.seconds);
    setTimer({
      ...timer,
      minutes: completed ? formatNumberToStr(minutes) : formatStr(str),
      counter: minutes * 60 + seconds,
    });
  };

  const setSeconds = (str: string, completed: boolean) => {
    const minutes = Number(timer.minutes);
    const seconds = formatStrToNumber(str);
    setTimer({
      ...timer,
      seconds: completed ? formatNumberToStr(seconds) : formatStr(str),
      counter: minutes * 60 + seconds,
    });
  };

  const setCounter = (counter: number) => {
    const seconds = counter % 60;
    const minutes = (counter - seconds) / 60;
    setTimer({
      minutes: formatNumberToStr(minutes),
      seconds: formatNumberToStr(seconds),
      counter,
    });
  };

  const clearTimeoutId = React.useCallback(() => {
    if (timerId.current) {
      clearTimeout(timerId.current);
      timerId.current = null;
    }
    audio.current = null;
  }, [timerId, audio]);

  React.useEffect(() => {
    return () => {
      clearTimeoutId();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isStart && timer.counter > 0) {
      const startTime = Date.now();
      const startCount = timer.counter;

      timerId.current = setInterval(() => {
        const d = Math.round((Date.now() - startTime) / unitSeconds);
        setCounter(startCount - d);
      }, unitSeconds);
    }
    if (!isStart && timerId.current) {
      clearTimeoutId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStart]);

  React.useEffect(() => {
    if (timer.counter <= 0 && timerId.current) {
      audio.current?.play();
      setIsStart(false);
      clearTimeoutId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer.counter]);

  return (
    <div
      className={clsx(
        styles.Timer,
        timer.counter === 0 && styles.Timer__timeEnd,
        props.className,
      )}
    >
      <div className={styles.btnClose}>
        <CloseIcon onClick={props.onClose} />
      </div>
      <div className={styles.inputTimerWrapper}>
        <div className={styles.inputTimerWrapper__timePicker}>
          <input
            type="text"
            placeholder="00"
            value={timer.minutes}
            onChange={e => setMinutes(e.target.value, false)}
            onBlur={e => setMinutes(e.target.value, true)}
            disabled={isStart}
          />
          :
          <input
            type="text"
            placeholder="00"
            value={timer.seconds}
            onChange={e => setSeconds(e.target.value, false)}
            onBlur={e => setSeconds(e.target.value, true)}
            disabled={isStart}
          />
        </div>
        <div className={styles.inputTimerWrapper__controlTime}>
          <ArrowIcon
            onClick={() => {
              if (!isStart) {
                setCounter(Math.min(timer.counter + 30, 3599));
              }
            }}
          />
          <ArrowIcon
            onClick={() => {
              if (!isStart) {
                setCounter(Math.max(timer.counter - 30, 0));
              }
            }}
          />
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          name={!isStart ? "スタート" : "ストップ"}
          variant={!isStart ? "outline" : "normal"}
          className={clsx(styles.actions__btn)}
          onClick={() => {
            if (!isStart && timer.counter > 0) {
              audio.current = new Audio(audioTimeEnd);
              setIsStart(true);
            } else if (isStart) {
              setIsStart(false);
            }
          }}
        />
        <Button
          name="リセット"
          variant="outline"
          className={styles.actions__btn}
          onClick={() => {
            setTimer(DEFAULT_TIMER);
            clearTimeoutId();
          }}
          disabled={isStart}
        />
      </div>
    </div>
  );
};

export default Timer;
